/* =========================================================================
   CAF redesign 2026-10 — product page (v3) behaviour        [pdp-page pass]
   Loaded by sections/product-template.liquid (defer, full PDP only). Styles: caf-rd-pdp.css.

   Theme-owned UI only. App DOM (Bot Blocker price, TeeInBlue, BeOne chat, Judge.me) is READ;
   it is never moved, re-parented or rewritten — classes go on theme wrappers.

   1. Price flash   — pale-yellow ~1s flash on the price block when its text changes after the
                      shopper picks a size/colour/amount (+ a polite "Price updated" announcement).
   2. Share         — dropdown under the gallery share button (≥799px); phones try the native
                      share sheet first and fall back to a bottom sheet. Copy link → "Link copied".
   3. Gallery       — "2 / 5" count, zoom button (opens the theme lightbox) and, on TeeInBlue
                      products, the "Sample design" chip. Added to `.product_gallery` only after
                      Flickity initialised (children present at init become slides).
   4. Lightbox      — product-gallery fancybox gets a dark viewer: counter, 48px close, 52px
                      arrows, thumbnail row (desktop). Uses fancybox's public API (close / next /
                      previous / jumpTo); the vendor file and its options are untouched.
   5. Contact tab   — "Chat with Cora" card opens the BeOne widget (clicks #chat-bubble) when the
                      widget is on the page; otherwise the card's link (/pages/contact) is followed.
   6. Gift card     — recipient email is checked before add to cart: empty → "Please enter the
                      recipient's email address", bad format → "That email doesn't look right".
   7. Price at size — "Price for 5.5 IN: $33.99" under the TeeInBlue size pills: two custom
                      properties + one attribute on the theme's panel; the line itself is CSS
                      (caf-rd-pdp-personalize.css §15). Shows the selected variant's price once
                      the price block shows it too.
   8. Native buy box — see §8 below.
   9. From checkout — `?caf_from=checkout` (the checkout free-shipping card's "Personalize it" /
                      "See it"): shows snippets/caf-from-checkout-hint.liquid above the buy box.
   ========================================================================= */
(function () {
  'use strict';

  if (window.CAFRdPdp) return;

  const doc = document;
  const SECTION = '.shopify-section--product-template';
  const PHONE_MQ = window.matchMedia('(max-width: 798px)');
  const raf = window.requestAnimationFrame
    ? window.requestAnimationFrame.bind(window)
    : (cb) => window.setTimeout(cb, 16);

  const onReady = (fn) => {
    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  };

  const isPhone = () => PHONE_MQ.matches;

  const throttled = (fn) => {
    let queued = false;
    return () => {
      if (queued) return;
      queued = true;
      raf(() => {
        queued = false;
        fn();
      });
    };
  };

  const svg = (body, size = 20, extra = '') =>
    `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"${extra}>${body}</svg>`;

  const ICONS = {
    zoom: svg('<circle cx="10.5" cy="10.5" r="6.5"/><path d="m20 20-4.8-4.8M10.5 7.5v6M7.5 10.5h6"/>', 20),
    close: svg('<path d="M6 6l12 12M18 6 6 18"/>', 22),
    prev: svg('<path d="m15 18-6-6 6-6"/>', 24),
    next: svg('<path d="m9 18 6-6-6-6"/>', 24),
    sparkle: svg('<path d="M12 3.5 13.6 9l5.4 1.6-5.4 1.6L12 17.6l-1.6-5.4L5 10.6 10.4 9z"/><path d="M19 3v3M17.5 4.5h3"/>', 15),
  };

  /* -----------------------------------------------------------------------
     1. Price flash
     ----------------------------------------------------------------------- */
  let shopperActed = false;
  const armPriceFlash = () => {
    shopperActed = true;
  };

  const initPriceFlash = () => {
    ['pointerdown', 'keydown', 'change'].forEach((type) => {
      doc.addEventListener(type, armPriceFlash, { capture: true, once: true, passive: true });
    });

    const blocks = doc.querySelectorAll(
      `${SECTION} .product__details--product-page .product-block--discount-price-app, ` +
        `${SECTION} .product__details--product-page .product-block--price`,
    );

    blocks.forEach((block) => {
      if (block.dataset.cafPriceFlash) return;
      block.dataset.cafPriceFlash = '1';

      const read = () => {
        const el =
          block.querySelector('.bb-discount-price__sale, [data-sale-price]') ||
          block.querySelector('.price-ui .money, .current_price, .modal_price');
        return el ? el.textContent.replace(/\s+/g, ' ').trim() : '';
      };

      let last = read();
      let live = null;
      let timer = 0;

      const check = () => {
        const now = read();
        if (!now || now === last) return;
        const firstValue = !last;
        last = now;
        // App hydration / currency formatting on load is not a shopper action — no flash.
        if (firstValue || !shopperActed) return;

        block.classList.remove('caf-price-flash');
        void block.offsetWidth; // restart the animation on quick successive changes
        block.classList.add('caf-price-flash');
        window.clearTimeout(timer);
        timer = window.setTimeout(() => block.classList.remove('caf-price-flash'), 1200);

        if (!live) {
          live = doc.createElement('span');
          live.className = 'visually-hidden caf-price-flash__live';
          live.setAttribute('aria-live', 'polite');
          block.appendChild(live);
        }
        live.textContent = `Price updated: ${now}`;
      };

      const tick = throttled(check);
      new MutationObserver((mutations) => {
        tick();
        // The only attribute watched is the variant id Bot Blocker writes on its root (§7).
        if (mutations.some((mutation) => mutation.type === 'attributes')) {
          queueSizePrice();
          queueNativeOptions();
        }
      }).observe(block, {
        attributeFilter: ['data-selected-variant-id'],
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true,
      });
    });
  };

  /* -----------------------------------------------------------------------
     2. Share
     ----------------------------------------------------------------------- */
  // `host` keeps the temporary textarea inside the open menu, so the fallback's focus jump does
  // not count as "focus left the menu" (which closes the desktop dropdown).
  const copyText = (value, host) => {
    const legacyCopy = () =>
      new Promise((resolve, reject) => {
        const ta = doc.createElement('textarea');
        ta.value = value;
        ta.setAttribute('readonly', '');
        ta.setAttribute('aria-hidden', 'true');
        ta.tabIndex = -1;
        ta.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;';
        (host || doc.body).appendChild(ta);
        ta.select();
        let ok = false;
        try {
          ok = doc.execCommand('copy');
        } catch (error) {
          ok = false;
        }
        ta.remove();
        if (ok) resolve();
        else reject(new Error('copy failed'));
      });

    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(value).catch(legacyCopy);
    }
    return legacyCopy();
  };

  const initShare = () => {
    doc.querySelectorAll('[data-caf-share]').forEach((root) => {
      if (root.dataset.cafShareBound) return;
      root.dataset.cafShareBound = '1';

      const trigger = root.querySelector('[data-caf-share-trigger]');
      const menu = root.querySelector('[data-caf-share-menu]');
      const scrim = root.querySelector('.caf-share__scrim');
      if (!trigger || !menu) return;

      const copyBtn = menu.querySelector('[data-caf-share-copy]');
      const copyLabel = menu.querySelector('[data-caf-share-copy-label]');
      const status = menu.querySelector('[data-caf-share-status]');
      const url = root.dataset.shareUrl || window.location.href.split('#')[0];
      const title = root.dataset.shareTitle || doc.title;
      const text = root.dataset.shareText || title;
      let open = false;

      const focusables = () =>
        Array.from(menu.querySelectorAll('a[href], button:not([disabled])')).filter(
          (el) => el.getClientRects().length > 0,
        );

      // Phones: the sheet lives on <body> while open so no gallery stacking context / transform
      // can trap the fixed sheet under the sticky header or the chat bubble.
      const placeMenu = (asSheet) => {
        const host = asSheet ? doc.body : root;
        if (scrim && scrim.parentElement !== host) host.appendChild(scrim);
        if (menu.parentElement !== host) host.appendChild(menu);
        menu.classList.toggle('caf-share__menu--sheet', asSheet);
      };

      const setOpen = (next, opts = {}) => {
        const moveFocus = opts.focus !== false;
        if (next === open) return;
        open = next;
        const asSheet = next && isPhone();
        if (next) placeMenu(asSheet);
        menu.hidden = !next;
        if (scrim) scrim.hidden = !asSheet;
        menu.setAttribute('aria-modal', asSheet ? 'true' : 'false');
        trigger.setAttribute('aria-expanded', String(next));
        root.classList.toggle('is-open', next);
        doc.documentElement.classList.toggle('caf-share-sheet-open', asSheet);
        if (next) {
          if (moveFocus) {
            const first = focusables()[0];
            if (first) first.focus({ preventScroll: true });
          }
        } else {
          placeMenu(false);
          if (moveFocus) trigger.focus({ preventScroll: true });
        }
      };

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (open) {
          setOpen(false);
          return;
        }
        if (isPhone() && typeof navigator.share === 'function') {
          navigator.share({ title, text, url }).catch((error) => {
            // Cancelled by the shopper → nothing. Unsupported / failed → our own sheet.
            if (!error || error.name !== 'AbortError') setOpen(true);
          });
          return;
        }
        setOpen(true);
      });

      [scrim, ...menu.querySelectorAll('[data-caf-share-close]')].forEach((el) => {
        if (!el) return;
        el.addEventListener('click', (event) => {
          event.preventDefault();
          setOpen(false);
        });
      });

      doc.addEventListener('click', (event) => {
        if (!open) return;
        if (root.contains(event.target) || menu.contains(event.target)) return;
        setOpen(false, { focus: false });
      });

      doc.addEventListener('keydown', (event) => {
        if (!open) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          setOpen(false);
          return;
        }
        if (event.key !== 'Tab' || !menu.classList.contains('caf-share__menu--sheet')) return;
        const items = focusables();
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && doc.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && doc.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });

      // Desktop dropdown closes when keyboard focus leaves it.
      menu.addEventListener('focusout', (event) => {
        if (!open || menu.classList.contains('caf-share__menu--sheet')) return;
        const next = event.relatedTarget;
        if (next && !menu.contains(next) && !root.contains(next)) setOpen(false, { focus: false });
      });

      menu.querySelectorAll('a.caf-share__row').forEach((link) => {
        link.addEventListener('click', () => {
          window.setTimeout(() => setOpen(false, { focus: false }), 200);
        });
      });

      if (copyBtn) {
        let resetTimer = 0;
        copyBtn.addEventListener('click', (event) => {
          event.preventDefault();
          copyText(url, menu)
            .then(() => {
              copyBtn.classList.add('is-done');
              if (copyLabel) copyLabel.textContent = 'Link copied';
              if (status) status.textContent = 'Link copied to your clipboard.';
            })
            .catch(() => {
              if (copyLabel) copyLabel.textContent = "Couldn't copy — use the address bar";
              if (status) status.textContent = "Couldn't copy the link.";
            })
            .finally(() => {
              if (open) copyBtn.focus({ preventScroll: true });
              window.clearTimeout(resetTimer);
              resetTimer = window.setTimeout(() => {
                copyBtn.classList.remove('is-done');
                if (copyLabel) copyLabel.textContent = 'Copy link';
                if (status) status.textContent = '';
              }, 2600);
            });
        });
      }

      const onViewportChange = () => {
        if (open) setOpen(false, { focus: false });
      };
      if (PHONE_MQ.addEventListener) PHONE_MQ.addEventListener('change', onViewportChange);
      else if (PHONE_MQ.addListener) PHONE_MQ.addListener(onViewportChange);
    });
  };

  /* -----------------------------------------------------------------------
     3. Gallery chrome (count, zoom, sample chip)
     ----------------------------------------------------------------------- */
  const mountGalleryUi = (wrap) => {
    const gallery = wrap.querySelector('.product_gallery[data-product-gallery]');
    if (!gallery || !gallery.classList.contains('flickity-enabled')) return;

    const cells = Array.from(gallery.querySelectorAll('.flickity-slider > .gallery-cell'));
    const total = cells.length;
    let ui = gallery.querySelector(':scope > .caf-gallery-ui');

    if (!ui) {
      ui = doc.createElement('div');
      ui.className = 'caf-gallery-ui';

      const chipText = wrap.getAttribute('data-caf-gallery-chip');
      if (chipText) {
        const chip = doc.createElement('span');
        chip.className = 'caf-gallery-chip';
        chip.innerHTML = `${ICONS.sparkle}<span></span>`;
        // "Sample design — yours shows your details": the part after the dash is its own span so small
        // phones (CSS, ≤389px) can show just "Sample design" instead of an ellipsis (2026-10-09).
        const label = chip.lastElementChild;
        const cut = chipText.indexOf(' — ');
        if (cut > 0) {
          label.textContent = chipText.slice(0, cut);
          const more = doc.createElement('span');
          more.className = 'caf-gallery-chip__more';
          more.textContent = chipText.slice(cut);
          label.appendChild(more);
        } else {
          label.textContent = chipText;
        }
        ui.appendChild(chip);
      }

      const count = doc.createElement('span');
      count.className = 'caf-gallery-count';
      count.setAttribute('aria-hidden', 'true');
      ui.appendChild(count);

      if (gallery.querySelector('a.lightbox')) {
        const zoom = doc.createElement('button');
        zoom.type = 'button';
        zoom.className = 'caf-gallery-zoom';
        zoom.setAttribute('aria-label', 'Zoom image');
        zoom.innerHTML = ICONS.zoom;
        zoom.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const link =
            gallery.querySelector('.gallery-cell.is-selected a.lightbox') || gallery.querySelector('a.lightbox');
          if (link) link.click();
        });
        ui.appendChild(zoom);
      }

      gallery.appendChild(ui);
    }

    const countEl = ui.querySelector('.caf-gallery-count');
    if (countEl) {
      const index = Math.max(0, cells.findIndex((cell) => cell.classList.contains('is-selected')));
      const label = total > 1 ? `${index + 1} / ${total}` : '';
      if (countEl.textContent !== label) countEl.textContent = label;
      countEl.hidden = total < 2;
    }
  };

  const initGallery = () => {
    doc.querySelectorAll(`${SECTION} [data-caf-gallery]`).forEach((wrap) => {
      if (wrap.dataset.cafGalleryBound) return;
      wrap.dataset.cafGalleryBound = '1';
      const tick = throttled(() => mountGalleryUi(wrap));
      tick();
      new MutationObserver(tick).observe(wrap, {
        attributeFilter: ['class'],
        attributes: true,
        childList: true,
        subtree: true,
      });
    });
  };

  /* -----------------------------------------------------------------------
     4. Lightbox (theme fancybox v3, product gallery only)
     ----------------------------------------------------------------------- */
  const thumbUrl = (src) => {
    if (!src) return '';
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&]width=)\d+/, '$1160');
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=160';
  };

  const isProductLightbox = (instance) => {
    if (!instance) return false;
    const trigger = instance.$trigger && instance.$trigger[0];
    if (trigger && trigger.closest && trigger.closest(`${SECTION} .product_gallery`)) return true;
    const base = (instance.opts && instance.opts.baseClass) || '';
    return base.indexOf('product-section__lightbox') > -1;
  };

  const updateLightbox = (instance) => {
    const refs = instance.__cafLb;
    if (!refs) return;
    const total = instance.group.length;
    const index = instance.currIndex;
    refs.count.textContent = total > 1 ? `${index + 1} / ${total}` : '';
    refs.thumbs.forEach((thumb, i) => {
      const on = i === index;
      thumb.classList.toggle('is-on', on);
      if (on) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  };

  const buildLightbox = (instance) => {
    const container = instance.$refs && instance.$refs.container && instance.$refs.container[0];
    if (!container || instance.__cafLb) return;
    const total = instance.group.length;
    container.classList.add('caf-lb');
    if (total < 2) container.classList.add('caf-lb--single');

    const top = doc.createElement('div');
    top.className = 'caf-lb__top';
    top.innerHTML =
      '<p class="caf-lb__meta"><span class="caf-lb__count" aria-live="polite"></span>' +
      '<span class="caf-lb__hint">Tap the photo to zoom</span></p>' +
      `<button type="button" class="caf-lb__close" aria-label="Close image viewer">${ICONS.close}</button>`;
    top.querySelector('.caf-lb__close').addEventListener('click', (event) => {
      event.preventDefault();
      instance.close();
    });
    container.appendChild(top);

    const thumbs = [];
    if (total > 1) {
      ['prev', 'next'].forEach((dir) => {
        const arrow = doc.createElement('button');
        arrow.type = 'button';
        arrow.className = `caf-lb__arrow caf-lb__arrow--${dir}`;
        arrow.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
        arrow.innerHTML = ICONS[dir];
        arrow.addEventListener('click', (event) => {
          event.preventDefault();
          if (dir === 'prev') instance.previous();
          else instance.next();
        });
        container.appendChild(arrow);
      });

      const row = doc.createElement('div');
      row.className = 'caf-lb__thumbs';
      instance.group.forEach((item, i) => {
        const orig = item.opts && item.opts.$orig && item.opts.$orig[0];
        const img = orig && orig.querySelector ? orig.querySelector('img') : null;
        const src = thumbUrl(item.thumb || (img && (img.currentSrc || img.getAttribute('src'))) || item.src);
        const button = doc.createElement('button');
        button.type = 'button';
        button.className = 'caf-lb__thumb';
        button.setAttribute('aria-label', `Show image ${i + 1} of ${total}`);
        if (src) {
          const thumbImg = doc.createElement('img');
          thumbImg.src = src;
          thumbImg.alt = '';
          thumbImg.loading = 'lazy';
          button.appendChild(thumbImg);
        }
        button.addEventListener('click', (event) => {
          event.preventDefault();
          instance.jumpTo(i);
        });
        row.appendChild(button);
        thumbs.push(button);
      });
      container.appendChild(row);
    }

    instance.__cafLb = { count: top.querySelector('.caf-lb__count'), thumbs };
    updateLightbox(instance);
  };

  const initLightbox = () => {
    const $ = window.jQuery;
    if (!$ || !$.fancybox || initLightbox.bound) return;
    initLightbox.bound = true;
    $(doc).on('onInit.fb', (event, instance) => {
      if (isProductLightbox(instance)) buildLightbox(instance);
    });
    $(doc).on('afterShow.fb beforeShow.fb', (event, instance) => {
      if (instance && instance.__cafLb) updateLightbox(instance);
    });
  };

  /* -----------------------------------------------------------------------
     5. Contact tab — chat card
     ----------------------------------------------------------------------- */
  const initContactChat = () => {
    doc.addEventListener('click', (event) => {
      const card = event.target && event.target.closest ? event.target.closest('[data-caf-contact-chat]') : null;
      if (!card) return;
      const bubble = doc.querySelector('#chat-widget-root #chat-bubble, .chat-widget #chat-bubble');
      if (!bubble) return; // no widget → follow the link (contact form)
      event.preventDefault();
      const win = doc.getElementById('chat-window');
      const isOpen = !!win && win.getBoundingClientRect().height > 0;
      if (!isOpen) bubble.click();
      else {
        const field = win.querySelector('textarea, input[type="text"]');
        if (field) field.focus();
      }
    });
  };

  /* -----------------------------------------------------------------------
     6. Gift card recipient email
     ----------------------------------------------------------------------- */
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const initGiftCard = () => {
    doc.querySelectorAll(`${SECTION} [data-recipient-form]`).forEach((recipientForm) => {
      const form = recipientForm.closest('form');
      if (!form || form.dataset.cafRecipientBound) return;
      form.dataset.cafRecipientBound = '1';

      const checkbox = form.querySelector('[data-recipient-disclosure-checkbox]');
      const email = recipientForm.querySelector('[data-recipient-form-email-input]');
      const message = recipientForm.querySelector('[data-recipient-form-error-message]');
      if (!checkbox || !email || !message) return;

      const MSG_EMPTY = message.dataset.cafMsgEmpty || "Please enter the recipient's email address";
      const MSG_BAD = message.dataset.cafMsgInvalid || "That email doesn't look right. Please check it.";
      // The server-side path (app.js adds .recipient-form--has-errors on a 422) reuses this text.
      message.textContent = MSG_BAD;

      const problem = () => {
        if (!checkbox.checked) return '';
        const value = email.value.trim();
        if (!value) return MSG_EMPTY;
        if (!EMAIL_RE.test(value)) return MSG_BAD;
        return '';
      };

      const clear = () => {
        recipientForm.classList.remove('recipient-form--has-errors', 'caf-recipient--invalid');
        email.removeAttribute('aria-invalid');
        message.removeAttribute('role');
      };

      const show = (text) => {
        message.textContent = text;
        message.setAttribute('role', 'alert');
        recipientForm.classList.add('recipient-form--has-errors', 'caf-recipient--invalid');
        email.setAttribute('aria-invalid', 'true');
      };

      const guard = (event) => {
        const text = problem();
        if (!text) {
          if (recipientForm.classList.contains('caf-recipient--invalid')) clear();
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        show(text);
        const details = recipientForm.closest('details');
        if (details && !details.open) details.open = true;
        email.focus({ preventScroll: true });
        email.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };

      // Capture phase: runs before app.js's delegated `.ajax-submit` handler (document, bubble).
      form.addEventListener(
        'click',
        (event) => {
          const target = event.target && event.target.closest ? event.target.closest('.add_to_cart, button[name="add"]') : null;
          if (target) guard(event);
        },
        true,
      );
      form.addEventListener('submit', guard, true);

      email.addEventListener('input', () => {
        if (!recipientForm.classList.contains('caf-recipient--invalid')) return;
        const text = problem();
        if (!text) clear();
        else if (message.textContent !== text) message.textContent = text;
      });
      checkbox.addEventListener('change', () => {
        if (!checkbox.checked) clear();
      });
    });
  };


  /* -----------------------------------------------------------------------
     Size guide next to the TeeInBlue "Size" option (owner 2026-10-09: Bot Blocker replaces Kiwi).
     TeeInBlue owns the Size option DOM (never moved) — we only add our own button to its title row
     and click the real Bot Blocker trigger (`.bb-size-guide__trigger`, app block in product.json).
     Our button is a sibling after the title (same pattern Kiwi used); app nodes are never moved.
     The original block is hidden via `html.caf-sg-inline` only while our button exists.
     ----------------------------------------------------------------------- */
  const RULER = svg('<path d="M3 15.5 15.5 3l5.5 5.5L8.5 21z"/><path d="m7 12 1.5 1.5M10 9l2 2M13 6l1.5 1.5"/>', 18);
  const syncSizeGuideButton = () => {
    const trigger = doc.querySelector('.bb-size-guide__trigger');
    // TeeInBlue's Size option, or (2026-10-13) the theme's own Size row on a Shopify listing — a row with a
    // real choice only; a hidden one-value row gets no link and the app's block stays where it is.
    const option =
      doc.querySelector('.tee-option--size') ||
      doc.querySelector(`${SECTION} .product__details--product-page.caf-standard-shopify-product .swatch.caf-swatch--size:not(.caf-swatch--single)`);
    const title = option && option.querySelector(':scope > .tee-option__title, :scope > .option_title');
    const existing = doc.querySelector('[data-caf-size-guide]');
    if (!trigger || !title || trigger.hidden) {
      if (existing) existing.remove();
      doc.documentElement.classList.remove('caf-sg-inline');
      return;
    }
    if (option.querySelector('[data-caf-size-guide]')) return;
    if (existing) existing.remove();
    const button = doc.createElement('button');
    button.type = 'button';
    button.className = 'caf-sg-link';
    button.setAttribute('data-caf-size-guide', '');
    button.setAttribute('aria-haspopup', 'dialog');
    button.innerHTML = `${RULER}<span>${(trigger.textContent || 'Size guide').trim()}</span>`;
    button.addEventListener('click', () => trigger.click());
    // Added next to TeeInBlue's title — nothing of the app is moved; CSS pins it to the title row.
    title.insertAdjacentElement('afterend', button);
    doc.documentElement.classList.add('caf-sg-inline');
  };
  const initSizeGuide = () => {
    syncSizeGuideButton();
    doc.addEventListener('teeinblue-event-component-injected', () => window.setTimeout(syncSizeGuideButton, 50));
    // TeeInBlue re-renders its options on product/variant switches; Bot Blocker reveals its trigger after fetching the chart.
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(() => { queued = false; syncSizeGuideButton(); });
    }).observe(doc.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
  };

  /* -----------------------------------------------------------------------
     7. Price where the size is chosen (owner 2026-10-12)
     A bigger size or another material changes the price, but on a phone the price block is far
     above the size pills. The line "Price for 5.5 IN: $33.99" is drawn under the pills by the two
     pseudo-elements of TeeInBlue's size wrapper (caf-rd-pdp-personalize.css §15). This script
     only writes their text, as --caf-size-price-label / --caf-size-price plus [data-caf-size-price],
     on the theme's own `.caf-pdp-custom-panel`. Nothing is added to or moved in the app's form.

     Where the price comes from — nothing is calculated or formatted here:
     - the selected variant = `?variant=` in the address, which TeeInBlue rewrites on every size /
       colour / product tap (the theme form's `[name="id"]` does not follow those taps);
     - its price = that variant's `sale` string in the price block's own JSON (Bot Blocker,
       `script[data-variant-prices]`): the text the block prints for one item, in the shop's money
       format. With quantity 2+ the block shows the total; this line stays the price of one;
     - the line is only written while that string equals the `sale` of the variant the block is
       showing (`data-selected-variant-id` on its root — on TeeInBlue pages Bot Blocker picks it
       by price, so it can be another variant with the same price) and while the variant's Size
       (product JSON on the theme's `.product_form`) is the pill that is ticked.
     So the line cannot disagree with the price block or pair a size with another size's price.
     Until the block has followed a tap (~0.1–0.4s) the previous, still true, pair stays; a block
     that never follows gets no line. No size pills, a single size, no price block or no variant
     data → no attribute, no line, no layout change.
     Runs when the price block changes variant (observer in §1), when TeeInBlue (re)injects its
     form and on a size / colour / product tap — no timer loop; every write is skipped when equal.
     ----------------------------------------------------------------------- */
  const SIZE_PRICE_ATTR = 'data-caf-size-price';
  const SIZE_PRICE_LABEL = '--caf-size-price-label';
  const SIZE_PRICE_VALUE = '--caf-size-price';
  // After a tap the address and the price block follow within ~0.4s. Two more looks, 250ms and 1.2s
  // after the last run, cover a slow phone; if the pair still does not add up, the line hides.
  const SIZE_PRICE_LOOKS = [250, 950];

  const cssString = (value) => `"${String(value).replace(/[\u0000-\u001f\u007f]+/g, ' ').replace(/[\\"]/g, '\\$&')}"`;

  // Variant id → "$33.99", parsed once per price block from its own JSON.
  const salePrices = new WeakMap();
  const salePriceOf = (root, id) => {
    if (!salePrices.has(root)) {
      const map = {};
      try {
        JSON.parse(root.querySelector('script[data-variant-prices]').textContent).forEach((entry) => {
          // A money format that carries markup cannot be shown through CSS `content` → no line for it.
          if (entry && entry.sale && String(entry.sale).indexOf('<') === -1) map[entry.id] = String(entry.sale).trim();
        });
      } catch (error) {
        /* no readable prices → no line */
      }
      salePrices.set(root, map);
    }
    return salePrices.get(root)[id] || '';
  };

  // Variant id → its Size value, parsed once from the product JSON the theme prints on the form.
  let variantSizes;
  const sizeOfVariant = (id) => {
    if (variantSizes === undefined) {
      variantSizes = null;
      try {
        const product = JSON.parse(doc.querySelector(`${SECTION} .product_form[data-product]`).getAttribute('data-product'));
        const index = product.options.findIndex((name) => String(name).trim().toLowerCase() === 'size');
        if (index > -1) {
          variantSizes = {};
          product.variants.forEach((variant) => {
            variantSizes[variant.id] = String(variant.options[index]).trim();
          });
        }
      } catch (error) {
        variantSizes = null;
      }
    }
    return variantSizes ? variantSizes[id] : undefined;
  };

  // What is on the panel now — every write below is skipped when the value is already there.
  let sizePricePanel = null;
  let sizePriceSize = '';
  let sizePriceAmount = '';
  let sizePriceTimer = 0;

  const hideSizePrice = (panel) => {
    sizePriceSize = '';
    sizePriceAmount = '';
    if (!panel.hasAttribute(SIZE_PRICE_ATTR)) return;
    panel.removeAttribute(SIZE_PRICE_ATTR);
    panel.style.removeProperty(SIZE_PRICE_LABEL);
    panel.style.removeProperty(SIZE_PRICE_VALUE);
  };

  const syncSizePrice = (look = 0) => {
    window.clearTimeout(sizePriceTimer);
    const details = doc.querySelector(`${SECTION} .product__details--product-page`);
    const panel = details && details.querySelector('.caf-pdp-custom-panel');
    if (!panel) return;

    const option = panel.querySelector('.tee-option--size:not(.sr-only)');
    const radios = option ? Array.from(option.querySelectorAll('input[type="radio"]')) : [];
    const picked = radios.length > 1 ? radios.find((radio) => radio.checked) : null;
    const size = picked ? String(picked.value || '').trim() : '';
    const root = size ? details.querySelector('.product-block--discount-price-app [data-discount-price-root]') : null;
    const blockId = root ? root.getAttribute('data-selected-variant-id') : '';
    const shown = blockId ? salePriceOf(root, blockId) : ''; // what the price block shows for one item

    if (!shown || sizeOfVariant(blockId) === undefined) {
      hideSizePrice(panel);
      return;
    }

    const inAddress = new URLSearchParams(window.location.search).get('variant');
    const id = inAddress && salePriceOf(root, inAddress) ? inAddress : blockId;
    if (sizeOfVariant(id) !== size || salePriceOf(root, id) !== shown) {
      // The address or the price block is still on the variant from before the tap. What is on screen is
      // still a true size + price pair, so leave it and look again; a block that never follows gets no line.
      if (look < SIZE_PRICE_LOOKS.length) sizePriceTimer = window.setTimeout(() => syncSizePrice(look + 1), SIZE_PRICE_LOOKS[look]);
      else hideSizePrice(panel);
      return;
    }

    // A panel we have not written to yet (first run, or the theme rebuilt it) gets everything once.
    const known = panel === sizePricePanel && panel.hasAttribute(SIZE_PRICE_ATTR);
    const newSize = !known || size !== sizePriceSize;
    const newPrice = !known || shown !== sizePriceAmount;
    if (!newSize && !newPrice) return;

    if (newSize) panel.style.setProperty(SIZE_PRICE_LABEL, cssString(`Price for ${size}:`));
    if (newPrice) panel.style.setProperty(SIZE_PRICE_VALUE, cssString(shown));
    // '' = shown without a highlight (page load, app hydration). After a shopper action a new price
    // flips a ↔ b, which restarts the 1s highlight on the price (two identical keyframes in the CSS).
    let flash = panel.getAttribute(SIZE_PRICE_ATTR) || '';
    if (known && newPrice && shopperActed) flash = flash === 'a' ? 'b' : 'a';
    if (panel.getAttribute(SIZE_PRICE_ATTR) !== flash) panel.setAttribute(SIZE_PRICE_ATTR, flash);

    sizePricePanel = panel;
    sizePriceSize = size;
    sizePriceAmount = shown;
  };

  const queueSizePrice = throttled(() => syncSizePrice());

  const initSizePrice = () => {
    queueSizePrice();
    // The app has just put its form in the page (once per load, not per keystroke): switch the line on in the
    // same task, before that form is painted for the first time, so the rows below the pills are not pushed
    // down a moment later. Two more looks in case a campaign draws its options after the event.
    doc.addEventListener('teeinblue-event-component-injected', () => {
      syncSizePrice();
      [50, 600].forEach((delay) => window.setTimeout(queueSizePrice, delay));
    });
    // A size, colour or product pill was tapped. With the same price only the size in the label changes and
    // it is written here; with a new price the block follows a moment later and the observer in §1 updates
    // the line — until then this run only starts the "did it follow?" looks described above.
    doc.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (target instanceof Element && target.matches('input[type="radio"]') && target.closest(`${SECTION} .tee-option`)) queueSizePrice();
      },
      { capture: true, passive: true },
    );
    // This file runs before the script that builds the panel; by `load` the panel and the price block exist.
    window.addEventListener('load', queueSizePrice, { once: true });
  };

  /* -----------------------------------------------------------------------
     8. Native buy box (owner 2026-10-13) — Shopify listings, plain or linked to TeeInBlue
     (`.product__details--product-page.caf-standard-shopify-product`, printed by Liquid). Look:
     assets/caf-rd-pdp-native.css. This part only writes text into theme nodes and clicks theme buttons.
     - Colour name beside the option title (`[data-caf-option-value]`, product-swatch.liquid).
     - "Price for S: $26.99" in `[data-caf-native-price]`: the variant of the theme form's `[name="id"]`
       and that variant's `sale` string from the price block's own JSON (same source as §7), so the line
       cannot disagree with the price at the top. No readable price → the line is hidden.
     - Buy now (`[data-caf-buy-now]`, product-form.liquid): sets `window.__cafBuyNow` and clicks the
       form's own Add to cart, so validation, TeeInBlue's fields and the add request are the ones of a
       normal add. `app.js` reads the flag after a successful add and goes to checkout instead of
       opening the drawer / sheet; a failed or blocked add leaves the shopper on the page.
     ----------------------------------------------------------------------- */
  const NATIVE = `${SECTION} .product__details--product-page.caf-standard-shopify-product`;
  const BUY_NOW_WAIT = 12000;
  let buyNowTimer = 0;

  const endBuyNow = () => {
    window.clearTimeout(buyNowTimer);
    window.__cafBuyNow = false;
    doc.querySelectorAll(`${NATIVE} [data-caf-buy-now].is-busy`).forEach((button) => {
      button.classList.remove('is-busy');
      button.removeAttribute('aria-busy');
      const label = button.querySelector('.caf-pdp-buynow__label');
      if (label && button.dataset.cafLabel) label.textContent = button.dataset.cafLabel;
    });
  };

  const syncNativeOptions = () => {
    const details = doc.querySelector(NATIVE);
    if (!details) return;

    details.querySelectorAll('.swatch_options [data-caf-option-value]').forEach((node) => {
      const row = node.closest('.swatch');
      const picked = row && row.querySelector('input[type="radio"]:checked');
      const text = picked ? `: ${picked.value}` : '';
      if (node.textContent !== text) node.textContent = text;
    });

    const line = details.querySelector('[data-caf-native-price]');
    if (!line) return;
    const form = line.closest('form');
    const idField = form && form.querySelector('[name="id"]');
    const root = details.querySelector('.product-block--discount-price-app [data-discount-price-root]');
    const amount = root && idField && idField.value ? salePriceOf(root, idField.value) : '';
    if (!amount) {
      if (!line.hidden) line.hidden = true;
      return;
    }
    const picked = line.closest('.swatch').querySelector('input[type="radio"]:checked');
    const label =
      line.hasAttribute('data-caf-native-price-size') && picked
        ? `Price for ${String(picked.value).replace(/\s*-\s*best selling\s*$/i, '').trim()}:`
        : 'Price for this selection:';
    let labelNode = line.querySelector('.caf-native-price__label');
    let amountNode = line.querySelector('.caf-native-price__amount');
    if (!labelNode || !amountNode) {
      line.textContent = '';
      labelNode = doc.createElement('span');
      labelNode.className = 'caf-native-price__label';
      amountNode = doc.createElement('b');
      amountNode.className = 'caf-native-price__amount';
      line.append(labelNode, ' ', amountNode);
    }
    if (labelNode.textContent !== label) labelNode.textContent = label;
    if (amountNode.textContent !== amount) amountNode.textContent = amount;
    if (line.hidden) line.hidden = false;
  };

  const queueNativeOptions = throttled(syncNativeOptions);

  const onBuyNow = (event) => {
    const button = event.target instanceof Element && event.target.closest(`${NATIVE} [data-caf-buy-now]`);
    if (!button || button.disabled || button.classList.contains('is-busy')) return;
    const form = button.closest('form');
    const atc = form && form.querySelector('button.add_to_cart');
    if (!atc || atc.disabled || atc.classList.contains('disabled')) return;
    event.preventDefault();

    if (!atc.classList.contains('ajax-submit')) {
      // Cart action is not "ajax" (theme setting): a plain form post, sent on to checkout by Shopify.
      let field = form.querySelector('input[name="return_to"]');
      if (!field) {
        field = doc.createElement('input');
        field.type = 'hidden';
        field.name = 'return_to';
        form.appendChild(field);
      }
      field.value = '/checkout';
      atc.click();
      return;
    }

    const label = button.querySelector('.caf-pdp-buynow__label');
    if (label && !button.dataset.cafLabel) button.dataset.cafLabel = label.textContent;
    window.__cafBuyNow = true;
    button.classList.add('is-busy');
    button.setAttribute('aria-busy', 'true');
    if (label) label.textContent = 'Opening checkout…';
    // A blocked add (TeeInBlue: a required choice is missing) sends no request and no event: give the
    // button back after a moment so the next plain Add to cart is not taken for a Buy now.
    window.clearTimeout(buyNowTimer);
    buyNowTimer = window.setTimeout(endBuyNow, BUY_NOW_WAIT);
    atc.click();
    // Nothing was sent (the app stopped the click): the button did not go busy → release at once.
    window.setTimeout(() => {
      if (window.__cafBuyNow && !atc.disabled && !atc.classList.contains('disabled')) endBuyNow();
    }, 400);
  };

  const initNativeBuyBox = () => {
    if (!doc.querySelector(NATIVE)) return;
    syncNativeOptions();
    doc.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (target instanceof Element && target.matches('input[type="radio"]') && target.closest(`${NATIVE} .swatch_options`)) {
          queueNativeOptions();
          // The theme's variant script runs after this capture-phase listener; look again once it has.
          window.setTimeout(queueNativeOptions, 120);
        }
      },
      { capture: true, passive: true },
    );
    ['variant:change', 'variant-change'].forEach((type) => doc.addEventListener(type, queueNativeOptions));
    window.addEventListener('load', queueNativeOptions, { once: true });
    doc.addEventListener('click', onBuyNow);
    // A failed add (app.js) or a page restored from the back/forward cache: Buy now is no longer pending.
    doc.addEventListener('caf:buy-now-ended', endBuyNow);
    window.addEventListener('pageshow', endBuyNow);
  };

  /* -----------------------------------------------------------------------
     9. From checkout (2026-10-17, CAF checkout Level 3 / F3 card v3) — the checkout card links here
     with `caf_from=checkout` in the same tab; since 2026-10-18 also the thank-you page's suggestions
     (`caf_from=thankyou&caf_pct=35`, through /discount/<code>), which show the hint's second text. Show the hint printed hidden above the buy box
     (`[data-caf-from-checkout-hint]`): finish here and Buy now goes straight back to checkout. The
     visit is remembered for this page path for 30 minutes (sessionStorage), so a reload after the
     theme rewrote the URL for a variant still shows it. Storage that throws → the URL alone decides.
     ----------------------------------------------------------------------- */
  const FROM_CHECKOUT_KEY = 'caf_from_checkout';
  const FROM_CHECKOUT_TTL = 30 * 60 * 1000;
  // 2026-10-18: the thank-you page's suggestions ("Make one for someone else") arrive the same way with
  // caf_from=thankyou (through /discount/<code>, so the code is already on) and caf_pct=<percent>.
  const FROM_SOURCES = ['checkout', 'thankyou'];

  // Only the thank-you code's real percent: anyone can type caf_pct=90 into a URL.
  const THANK_YOU_PERCENTS = [35];
  const readPercent = (value) => {
    const number = Number(value);
    return THANK_YOU_PERCENTS.includes(number) ? number : null;
  };

  // { from: 'checkout' | 'thankyou', pct } for a visit from a checkout card or the thank-you page, else null.
  const cameFromCheckout = () => {
    let fromUrl = null;
    let pctUrl = null;
    try {
      const params = new URLSearchParams(window.location.search);
      const from = params.get('caf_from');
      if (FROM_SOURCES.includes(from)) {
        fromUrl = from;
        pctUrl = readPercent(params.get('caf_pct'));
      }
    } catch (error) {
      fromUrl = null;
    }
    const path = window.location.pathname;
    try {
      if (fromUrl) {
        window.sessionStorage.setItem(FROM_CHECKOUT_KEY, JSON.stringify({ path, at: Date.now(), from: fromUrl, pct: pctUrl }));
        return { from: fromUrl, pct: pctUrl };
      }
      const saved = JSON.parse(window.sessionStorage.getItem(FROM_CHECKOUT_KEY) || 'null');
      if (!saved || saved.path !== path || !(Date.now() - Number(saved.at) < FROM_CHECKOUT_TTL)) return null;
      // Visits stored before 2026-10-18 have no `from`: they came from checkout.
      const from = FROM_SOURCES.includes(saved.from) ? saved.from : 'checkout';
      return { from, pct: readPercent(saved.pct) };
    } catch (error) {
      return fromUrl ? { from: fromUrl, pct: pctUrl } : null;
    }
  };

  const initFromCheckout = () => {
    const hint = doc.querySelector(`${SECTION} [data-caf-from-checkout-hint]`);
    if (!hint) return;
    const visit = cameFromCheckout();
    if (!visit) return;
    hint.querySelectorAll('[data-caf-hint-from]').forEach((text) => {
      text.hidden = text.getAttribute('data-caf-hint-from') !== visit.from;
    });
    const code = hint.querySelector('[data-caf-hint-code]');
    if (code && visit.from === 'thankyou' && visit.pct) code.textContent = `Your ${visit.pct}% code is on.`;
    hint.hidden = false;
  };

  /* ----------------------------------------------------------------------- */
  const init = () => {
    if (!doc.querySelector(SECTION)) return;
    initPriceFlash();
    initShare();
    initGallery();
    initLightbox();
    initContactChat();
    initGiftCard();
    initSizeGuide();
    initSizePrice();
    initNativeBuyBox();
    initFromCheckout();
  };

  window.CAFRdPdp = { init };
  onReady(init);
  // jQuery / fancybox load later than this file on some routes (all defer) — retry once loaded.
  window.addEventListener('load', initLightbox, { once: true });
})();
